/* Go Find Les — Theme JavaScript */
'use strict';

/* ── Mobile nav ─────────────────────────────────────────── */
function initMobileNav() {
  const btn = document.querySelector('.mobile-menu-btn');
  const nav = document.querySelector('.mobile-nav');
  const overlay = document.querySelector('.mobile-nav__overlay');
  const closeBtn = document.querySelector('.mobile-nav__close');
  if (!btn || !nav) return;

  function open() {
    nav.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }
  function close() {
    nav.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  btn.addEventListener('click', open);
  overlay?.addEventListener('click', close);
  closeBtn?.addEventListener('click', close);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
}

/* ── FAQ accordion ───────────────────────────────────────── */
function initFaq() {
  document.querySelectorAll('.faq-item__q').forEach((q) => {
    q.addEventListener('click', () => {
      const item = q.closest('.faq-item');
      const isOpen = item.classList.contains('open');
      document.querySelectorAll('.faq-item').forEach((i) => i.classList.remove('open'));
      if (!isOpen) item.classList.add('open');
    });
  });
}

/* ── Product gallery ─────────────────────────────────────── */
function initProductGallery() {
  const thumbs = document.querySelectorAll('.product-gallery__thumb');
  const main = document.querySelector('.product-gallery__main img');
  if (!thumbs.length || !main) return;

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      thumbs.forEach((t) => t.classList.remove('active'));
      thumb.classList.add('active');
      const src = thumb.querySelector('img')?.src;
      if (src) main.src = src;
    });
  });
}

/* ── Quantity buttons ────────────────────────────────────── */
function initQtyButtons() {
  document.querySelectorAll('.qty-input').forEach((wrap) => {
    const input = wrap.querySelector('.qty-num');
    const dec = wrap.querySelector('[data-qty="dec"]');
    const inc = wrap.querySelector('[data-qty="inc"]');
    if (!input) return;

    dec?.addEventListener('click', () => {
      const val = parseInt(input.value, 10);
      if (val > 1) input.value = val - 1;
    });
    inc?.addEventListener('click', () => {
      input.value = parseInt(input.value, 10) + 1;
    });
  });
}

/* ── Newsletter ──────────────────────────────────────────── */
/*
  Previously this called preventDefault() and faked a "Subscribed!" state, which
  blocked the real {% form 'customer' %} from submitting — no signup ever
  reached Shopify. The form now posts normally and newsletter.liquid renders the
  real success and error states.
*/

/* ── Sticky header height CSS var ───────────────────────── */
function setHeaderHeight() {
  const header = document.querySelector('.site-header');
  if (header) {
    const update = () => {
      document.documentElement.style.setProperty(
        '--header-height',
        header.offsetHeight + 'px'
      );
    };
    update();
    window.addEventListener('resize', update);
  }
}

/* ── Collection sorting ──────────────────────────────────── */
/*
  Replaces initFilters(), which toggled an .active class on dead <button>s and
  filtered nothing. The category pills are real links now; this makes the sort
  dropdown actually re-request the collection.
*/
function initSort() {
  const sel = document.querySelector('#sort-by');
  if (!sel) return;
  sel.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sel.value);
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  });
}

/* ── Add to cart ─────────────────────────────────────────── */
/*
  This used to be a mock: it bound a click handler to .add-to-cart-btn and
  called preventDefault(), which blocked the REAL product form from ever
  submitting. Nothing could be added to the cart. The product form now submits
  normally; only the card quick-add is intercepted, and it posts to the Cart API
  for real.
*/
async function refreshCartCount() {
  try {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) return;
    const cart = await res.json();
    document.querySelectorAll('.cart-count').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  } catch (_) { /* count is cosmetic — never block the sale over it */ }
}

function initQuickAdd() {
  document.querySelectorAll('.product-card__quick').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      /* The card is wrapped in a link to the product, so stop the navigation. */
      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.dataset.variantId;
      if (!variantId) {
        /* No variant to add — send them to the product page rather than lie. */
        const link = btn.closest('a')?.href;
        if (link) window.location.href = link;
        return;
      }

      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || 'Could not add to cart');
        }
        btn.textContent = 'Added ✓';
        await refreshCartCount();
      } catch (err) {
        btn.textContent = 'Unavailable';
        console.error('[cart]', err);
      } finally {
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1600);
      }
    });
  });
}

/* ── Booking flow ────────────────────────────────────────── */
function initBooking() {
  const root = document.querySelector('[data-booking]');
  if (!root) return;

  const steps = Array.from(root.querySelectorAll('.booking-step'));
  const progress = Array.from(root.querySelectorAll('.booking-progress__step'));
  const panels = Array.from(root.querySelectorAll('.booking-panel'));
  const radios = Array.from(root.querySelectorAll('input[name="contact[Booking type]"]'));
  if (steps.length < 2) return;

  // JS is running, so take over the step display.
  root.classList.add('js-steps');
  const progressRail = root.querySelector('[data-booking-progress]');
  if (progressRail) progressRail.removeAttribute('aria-hidden');

  let current = 1;

  /* Inputs inside a hidden panel must not submit or block validation. */
  function syncPanels() {
    const checked = radios.find((r) => r.checked);
    const want = checked ? checked.dataset.service : null;

    panels.forEach((panel) => {
      const on = panel.dataset.panel === want;
      panel.hidden = !on;
      panel.querySelectorAll('input, select, textarea').forEach((field) => {
        field.disabled = !on;
      });
    });
    updateEstimate();
  }

  function money(n) {
    return '$' + n.toLocaleString('en-US');
  }

  function updateEstimate() {
    const box = root.querySelector('[data-estimate]');
    const input = root.querySelector('[data-estimate-input]');
    if (!box || !input || input.disabled) return;

    const unit = parseFloat(box.dataset.unitPrice) || 125;
    const bulkQty = parseInt(box.dataset.bulkQty, 10) || 0;
    const bulkUnit = parseFloat(box.dataset.bulkPrice) || unit;

    const qty = Math.max(1, Math.min(parseInt(input.value, 10) || 1, 40));
    const rate = bulkQty && qty >= bulkQty ? bulkUnit : unit;
    const total = qty * rate;

    const valueEl = box.querySelector('[data-estimate-value]');
    const noteEl = box.querySelector('[data-estimate-note]');
    if (valueEl) {
      valueEl.textContent =
        qty === 1 ? money(total) : money(total) + '  ·  ' + qty + ' × ' + money(rate);
    }
    if (noteEl && bulkQty && qty >= bulkQty && rate < unit) {
      noteEl.dataset.base = noteEl.dataset.base || noteEl.textContent;
      noteEl.textContent =
        'Bulk rate applied — ' + money(rate) + ' per window at ' + bulkQty + '+. ' + noteEl.dataset.base;
    } else if (noteEl && noteEl.dataset.base) {
      noteEl.textContent = noteEl.dataset.base;
    }
    box.hidden = false;
  }

  function clearError(group) {
    group.classList.remove('has-error');
    group.querySelector('.form-error')?.remove();
  }

  function showError(group, msg) {
    clearError(group);
    group.classList.add('has-error');
    const p = document.createElement('p');
    p.className = 'form-error';
    p.textContent = msg;
    group.appendChild(p);
  }

  /* Validate only the fields that are visible on this step. */
  function validate(stepEl) {
    let firstBad = null;

    stepEl.querySelectorAll('[data-req]').forEach((field) => {
      const group = field.closest('.form-group');
      if (!group || field.disabled || field.offsetParent === null) return;
      clearError(group);

      const value = (field.value || '').trim();
      if (!value) {
        showError(group, 'This one’s needed to price your job.');
        firstBad = firstBad || field;
        return;
      }
      if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        showError(group, 'That email address doesn’t look right.');
        firstBad = firstBad || field;
      }
    });

    if (firstBad) {
      firstBad.focus();
      firstBad.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    return true;
  }

  function go(n) {
    current = Math.max(1, Math.min(n, steps.length));
    steps.forEach((s) => s.classList.toggle('is-active', Number(s.dataset.step) === current));
    progress.forEach((p) => {
      const i = Number(p.dataset.progress);
      p.classList.toggle('is-active', i === current);
      p.classList.toggle('is-done', i < current);
    });

    const heading = steps[current - 1].querySelector('.booking-step__title');
    heading?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  root.querySelectorAll('[data-booking-next]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const stepEl = steps[current - 1];
      if (!validate(stepEl)) return;
      go(current + 1);
    });
  });

  root.querySelectorAll('[data-booking-back]').forEach((btn) => {
    btn.addEventListener('click', () => go(current - 1));
  });

  radios.forEach((r) => r.addEventListener('change', syncPanels));
  root.querySelector('[data-estimate-input]')?.addEventListener('input', updateEstimate);

  /* Final guard: never submit a step the browser can't see. */
  root.querySelector('form')?.addEventListener('submit', (e) => {
    if (!validate(steps[steps.length - 1])) e.preventDefault();
  });

  syncPanels();
  go(1);

  /* Came back from a successful post? Show the confirmation, not step 1. */
  if (root.querySelector('.booking-success')) {
    root.querySelectorAll('.booking-step, .booking-progress').forEach((el) => {
      el.style.display = 'none';
    });
  }
}

/* ── Init ────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  initMobileNav();
  initFaq();
  initProductGallery();
  initQtyButtons();
  setHeaderHeight();
  initSort();
  initQuickAdd();
  initBooking();
});
